import Stripe from 'stripe';
import _ from 'lodash';
import moment from 'moment';
import { axiosClient } from '../../api/axiosClient';

export const PRODUCT_PRO_PLAN =
  process.env.REACT_APP_STRIPE_PRODUCT_PRO_PLAN || '';
export const PRODUCT_TEAM_PLAN =
  process.env.REACT_APP_STRIPE_PRODUCT_TEAM_PLAN || '';

export const BASE_PRODUCTS = [PRODUCT_PRO_PLAN, PRODUCT_TEAM_PLAN];

// Metadata-based product identification
const BASE_PLAN_CATEGORIES = ['personal', 'team'];

type ProductLike = { id: string; metadata?: Record<string, string> | null };

export const isBasePlanProduct = (product: ProductLike): boolean =>
  BASE_PRODUCTS.includes(product.id) ||
  BASE_PLAN_CATEGORIES.includes(product.metadata?.product_category ?? '');

export const isAddonProduct = (product: ProductLike): boolean =>
  product.metadata?.type === 'credit_allowance';

/**
 * Read credits_monthly from price metadata first, falling back to product metadata.
 * Returns undefined when neither source has the value.
 */
export const getCreditsMonthly = (
  price: { metadata?: Record<string, string> | null } | null | undefined,
  product: { metadata?: Record<string, string> | null } | null | undefined,
  defaultValue?: number
): number | undefined => {
  type Meta = Record<string, string> | undefined;
  const raw =
    (price?.metadata as Meta)?.credits_monthly ||
    (product?.metadata as Meta)?.credits_monthly;
  if (raw) return parseInt(raw, 10);
  return defaultValue;
};

export interface PriceWithRecurring extends Omit<Stripe.Price, 'recurring'> {
  recurring: NonNullable<Stripe.Price['recurring']>;
}

export interface InvoiceItem {
  id?: string;
  price?: string;
  quantity: number;
}

export interface SimpleCommonInvoiceSubscriptionItem {
  id?: string;
  price?: string;
  quantity: number;
}

const findSubItem = (
  sub: Stripe.Subscription | null,
  { productId, priceId }: { productId?: string; priceId?: string }
) => {
  if (!sub) return null;
  return (
    sub.items.data.find((item) => {
      if (productId && item.price.product !== productId) {
        return false;
      }
      if (priceId && item.price.id !== priceId) {
        return false;
      }
      return true;
    }) || null
  );
};

const callApiToCreatePreviewInvoice = async (
  reqBody: Stripe.InvoiceCreatePreviewParams
) => {
  try {
    const response = await axiosClient.post(
      '/invoices/create-preview',
      reqBody
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create preview invoice', error);
    throw error;
  }
};
interface GetMatchingAddonPriceParams {
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string | undefined;
  selectedPlanInterval: string | undefined;
}

// Pick the highest pricing_version from a list of prices.
const _pickHighestVersion = (prices: Stripe.Price[]) =>
  prices.reduce((best, price) => {
    const bestVersion = parseInt(
      (best.metadata as Record<string, string>)?.pricing_version || '0',
      10
    );
    const priceVersion = parseInt(
      (price.metadata as Record<string, string>)?.pricing_version || '0',
      10
    );
    return priceVersion > bestVersion ? price : best;
  }, prices[0]);

// Get the addon price that matches the selected plan price.
//
// Selection priority:
// 1. Direct link: addon price with metadata.base_price === selectedPlanPriceId
//    (validates that billing intervals also match)
// 2. Interval match: active addon prices whose billing interval matches the
//    plan's billing interval, preferring the highest pricing_version
export const _getMatchingAddonPrice = ({
  addonPrices,
  selectedPlanPriceId,
  selectedPlanInterval,
}: GetMatchingAddonPriceParams) => {
  if (!selectedPlanPriceId || !selectedPlanInterval || !addonPrices.length)
    return null;

  // 1. Direct price-to-price link via base_price metadata
  const directMatch = addonPrices.find((addonPrice) => {
    const meta = addonPrice.metadata as Record<string, string>;
    return (
      addonPrice.active &&
      meta?.base_price === selectedPlanPriceId &&
      addonPrice?.recurring?.interval === selectedPlanInterval
    );
  });
  if (directMatch) return directMatch;

  // 2. Fall back to interval matching with highest pricing_version
  const intervalMatches = addonPrices.filter(
    (addonPrice) =>
      addonPrice.active &&
      addonPrice?.recurring?.interval === selectedPlanInterval
  );
  if (!intervalMatches.length) return null;

  return _pickHighestVersion(intervalMatches);
};

export interface GetInvoiceLinesParams {
  productId?: string;
  nonZeroAmount?: boolean;
}

interface ConvertSubscriptionItemsParamsToPreviewInvoiceLines {
  subscription: Stripe.Subscription | null;
  planPrices: Stripe.Price[];
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string;
  newAddonQuantity: number;
}

const _createInvoiceItemForPlan = ({
  subscription,
  planPrices,
}: {
  subscription: Stripe.Subscription | null;
  planPrices: Stripe.Price[];
}) => {
  // Identify the plan subscription item. Use env var IDs first, then fall back
  // to matching by the plan product ID derived from planPrices.
  const planProductId = planPrices[0]?.product as string | undefined;
  const subItem = subscription?.items.data.find((item) => {
    const productId = item.price.product as string;
    return BASE_PRODUCTS.includes(productId) || productId === planProductId;
  });

  if (subItem) {
    const subPriceId = subItem.price?.id;
    const isActiveInCatalog = planPrices.some(
      (p) => p.id === subPriceId && p.active
    );
    // Include price when active; omit when archived so Stripe keeps the
    // current price without rejecting it as inactive.
    const invoiceItem: InvoiceItem = { id: subItem.id, quantity: 1 };
    if (isActiveInCatalog) {
      invoiceItem.price = subPriceId;
    }
    return { subItem, invoiceItem };
  }
  // No existing item: find an active price for the default interval.
  const intervalMatchingPrice = planPrices.find(
    (price) => price.recurring?.interval === 'month'
  );
  return {
    subItem,
    invoiceItem: {
      price: intervalMatchingPrice?.id,
      quantity: 1,
    } as InvoiceItem,
  };
};

const _createInvoiceItemForAddon = ({
  subscription,
  addonPrices,
  selectedPlanPriceId,
  newAddonQuantity,
}: {
  subscription: Stripe.Subscription | null;
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string;
  newAddonQuantity: number;
}) => {
  // Identify the addon subscription item by the addon product ID from the catalog.
  const addonProductId = addonPrices[0]?.product as string | undefined;
  const subItemForAddon = addonProductId
    ? findSubItem(subscription, { productId: addonProductId })
    : null;
  const subItemIdForAddon = subItemForAddon?.id;

  // When the subscription already has an addon item, use its price.
  // Only fall back to catalog matching for first-time purchases.
  // Derive interval from the plan item (not items[0] which could be the addon).
  const planSubItem = subscription?.items?.data?.find((item) =>
    BASE_PRODUCTS.includes(item.price.product as string)
  );
  const selectedPlanInterval =
    planSubItem?.price?.recurring?.interval || 'month';
  const matchingAddonPrice = subItemForAddon
    ? addonPrices.find((p) => p.id === subItemForAddon.price.id) ||
      _getMatchingAddonPrice({
        addonPrices,
        selectedPlanPriceId,
        selectedPlanInterval,
      })
    : _getMatchingAddonPrice({
        addonPrices,
        selectedPlanPriceId,
        selectedPlanInterval,
      });
  if (matchingAddonPrice) {
    const invoiceItem = {
      id: subItemIdForAddon,
      price: matchingAddonPrice.id,
      quantity: newAddonQuantity,
    } as InvoiceItem;
    return {
      subItem: subItemForAddon,
      invoiceItem,
    };
  }
  return {};
};

const _convertSubscriptionItemsToPreviewInvoiceLines = ({
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  newAddonQuantity,
}: ConvertSubscriptionItemsParamsToPreviewInvoiceLines) => {
  // Items for membership plan
  const { invoiceItem: invoiceItemForPlan } = _createInvoiceItemForPlan({
    subscription,
    planPrices,
  });

  // Items for credits
  const { invoiceItem: invoiceItemForAddOn } = _createInvoiceItemForAddon({
    subscription,
    addonPrices,
    selectedPlanPriceId,
    newAddonQuantity,
  });

  return [
    invoiceItemForPlan,
    invoiceItemForAddOn,
  ] as SimpleCommonInvoiceSubscriptionItem[];
};

interface CalInvoicePreviewTodayParams {
  customerId: string;
  subscription: Stripe.Subscription | null;
  planPrices: Stripe.Price[];
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string;
  formerAddonQuantity: number;
  newAddonQuantity: number;
}

const _calculateInvoicePreviewTodayFromSubscription = async ({
  customerId,
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  formerAddonQuantity,
  newAddonQuantity,
}: CalInvoicePreviewTodayParams) => {
  const invoiceLines = _convertSubscriptionItemsToPreviewInvoiceLines({
    subscription,
    planPrices,
    addonPrices,
    selectedPlanPriceId,
    newAddonQuantity,
  });
  const increasingCredits = newAddonQuantity > formerAddonQuantity;
  const _reqData = {
    customer: customerId,
    subscription: subscription?.id,
    subscription_details: {
      // Use billing mode of the existing subscription if it exists
      billing_mode: subscription ? undefined : { type: 'flexible' },
      items: invoiceLines,
    },
    preview_mode: 'next',
  } as Stripe.InvoiceCreatePreviewParams;

  if (subscription && _reqData.subscription_details) {
    if (subscription.status === 'trialing') {
      _reqData.subscription_details.trial_end = 'now';
    }
    if (increasingCredits) {
      _reqData.subscription_details.billing_cycle_anchor = 'now';
      _reqData.subscription_details.proration_behavior = 'create_prorations';
    } else {
      _reqData.subscription_details.billing_cycle_anchor = 'unchanged';
      _reqData.subscription_details.proration_behavior = 'none';
    }
  }

  const invoice = await callApiToCreatePreviewInvoice(
    _reqData as Stripe.InvoiceCreatePreviewParams
  );
  return {
    invoice,
    invoiceLines,
  };
};

const _calculateSchedulePhasesForInvoicePreview = ({
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  formerAddonQuantity,
  newAddonQuantity,
  prorationBehavior,
}: {
  subscription: Stripe.Subscription;
  planPrices: Stripe.Price[];
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string;
  formerAddonQuantity: number;
  newAddonQuantity: number;
  prorationBehavior: string;
}) => {
  const { subItem: subItemForPlan, invoiceItem: invoiceItemForPlan } =
    _createInvoiceItemForPlan({
      subscription,
      planPrices,
    });
  const { invoiceItem: invoiceItemForAddOn } = _createInvoiceItemForAddon({
    subscription,
    addonPrices,
    selectedPlanPriceId,
    newAddonQuantity,
  });

  const originSubItems = subscription.items.data;
  const startOfThisPeriod = originSubItems[0]?.current_period_start;
  const endOfThisPeriod = originSubItems[0]?.current_period_end;

  const phase1Items = [];
  const phase2Items = [];
  if (invoiceItemForPlan) {
    // Schedule phases require a price. invoiceItemForPlan may omit price for
    // archived legacy prices, so read it from the subscription item directly.
    const planPhaseItem = {
      price: invoiceItemForPlan.price || subItemForPlan?.price?.id,
      quantity: invoiceItemForPlan.quantity,
    };
    phase1Items.push(planPhaseItem);
    phase2Items.push(planPhaseItem);
  }
  if (invoiceItemForAddOn) {
    const _invoiceItemForAddOn = _.omit(invoiceItemForAddOn, 'id');
    phase1Items.push({
      ..._invoiceItemForAddOn,
      quantity: formerAddonQuantity,
    });
    phase2Items.push({
      ..._invoiceItemForAddOn,
      quantity: newAddonQuantity,
    });
  }

  return [
    {
      proration_behavior: prorationBehavior,
      start_date: startOfThisPeriod,
      end_date: endOfThisPeriod,
      items: phase1Items,
    },
    {
      proration_behavior: prorationBehavior,
      start_date: endOfThisPeriod,
      // to make sure schedule can be considered correctly by Stripe for preview invoice
      end_date: moment().add(5, 'year').unix(),
      items: phase2Items,
    },
  ];
};

const _calculateInvoicePreviewTodayFromSchedule = async ({
  customerId,
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  formerAddonQuantity,
  newAddonQuantity,
}: CalInvoicePreviewTodayParams) => {
  if (!subscription) {
    throw new Error('Subscription is required');
  }

  const increasingCredits = newAddonQuantity > formerAddonQuantity;

  let prorationBehavior = 'none';
  if (increasingCredits) {
    prorationBehavior = 'create_prorations';
  } else {
    prorationBehavior = 'none';
  }

  const phases = _calculateSchedulePhasesForInvoicePreview({
    subscription,
    planPrices,
    addonPrices,
    selectedPlanPriceId,
    formerAddonQuantity,
    newAddonQuantity,
    prorationBehavior,
  });

  const reqData = {
    customer: customerId,
    preview_mode: 'next',
    schedule: (subscription?.schedule as unknown as { id: string })?.id,
    schedule_details: {
      proration_behavior: prorationBehavior,
      phases,
    },
  };

  const invoiceLines = phases[phases.length - 1].items.map((item) => {
    return {
      id: findSubItem(subscription, { priceId: item.price })?.id,
      ..._.pick(item, ['price', 'quantity']),
    };
  });
  const invoice = await callApiToCreatePreviewInvoice(
    reqData as Stripe.InvoiceCreatePreviewParams
  );
  return {
    invoice,
    invoiceLines,
  };
};

export const caculatelInvoicePreviewToday = async ({
  customerId,
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  formerAddonQuantity,
  newAddonQuantity,
}: CalInvoicePreviewTodayParams) => {
  const increasingCredits = newAddonQuantity > formerAddonQuantity;

  // Use the schedule-based preview only when the subscription already has a
  // schedule AND all subscription prices are active. Archived prices can't be
  // used in schedule phase items, so legacy subscriptions fall back to the
  // regular preview path (which omits price for existing items).
  const allPricesActive = subscription?.items?.data?.every(
    (item) => item.price?.active !== false
  );
  if (subscription?.schedule && !increasingCredits && allPricesActive) {
    return _calculateInvoicePreviewTodayFromSchedule({
      customerId,
      subscription,
      planPrices,
      addonPrices,
      selectedPlanPriceId,
      formerAddonQuantity,
      newAddonQuantity,
    });
  } else {
    return _calculateInvoicePreviewTodayFromSubscription({
      customerId,
      subscription,
      planPrices,
      addonPrices,
      selectedPlanPriceId,
      formerAddonQuantity,
      newAddonQuantity,
    });
  }
};

interface CalInvoicePreviewForNextTimeParams {
  customerId: string;
  subscription: Stripe.Subscription | null;
  planPrices: Stripe.Price[];
  addonPrices: Stripe.Price[];
  selectedPlanPriceId: string;
  newAddonQuantity: number;
}

export const calculateInvoicePreviewForNextTime = async ({
  customerId,
  subscription,
  planPrices,
  addonPrices,
  selectedPlanPriceId,
  newAddonQuantity,
}: CalInvoicePreviewForNextTimeParams) => {
  const invoiceLines = _convertSubscriptionItemsToPreviewInvoiceLines({
    subscription,
    planPrices,
    addonPrices,
    selectedPlanPriceId,
    newAddonQuantity,
  });
  const _reqData = {
    customer: customerId,
    subscription: subscription?.id,
    subscription_details: {
      // Use billing mode of the existing subscription if it exists
      billing_mode: subscription ? undefined : { type: 'flexible' },
      items: invoiceLines,
      billing_cycle_anchor: 'unchanged',
      proration_behavior: 'none',
    },
    preview_mode: 'next',
  } as Stripe.InvoiceCreatePreviewParams;
  const invoice = await callApiToCreatePreviewInvoice(_reqData);
  return invoice;
};

// Helper function to format currency
export const formatCurrency = (
  _amount: number | undefined | null,
  currency = 'usd'
) => {
  const amount = _amount || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
};

const getSubItemPendingQuantity = (
  subItem: Stripe.SubscriptionItem | undefined
) => {
  if (!subItem) return 0;
  if (subItem?.metadata?.pending_quantity) {
    return parseInt(subItem.metadata.pending_quantity, 10);
  }
  return subItem?.quantity ?? 0;
};

export const getPendingQuantity = (
  subscription: Stripe.Subscription,
  productId: string
) => {
  const subItem = subscription.items.data.find((item) => {
    return item.price.product === productId;
  });
  return getSubItemPendingQuantity(subItem);
};

const getSubItemRealQuantity = (
  subItem: Stripe.SubscriptionItem | undefined
) => {
  return subItem?.quantity ?? 0;
};

export const getRealQuantity = (
  subscription: Stripe.Subscription,
  productId: string
) => {
  const subItem = subscription.items.data.find((item) => {
    return item.price.product === productId;
  });
  return getSubItemRealQuantity(subItem);
};

export const convertInvoiceToSubscriptionItems = ({
  invoice,
  subscription,
}: {
  invoice: Stripe.Invoice;
  subscription: Stripe.Subscription | null;
}) => {
  const existingSubItemIds =
    subscription?.items.data.map((item) => item.id) || [];

  const keptInvoiceLines = invoice.lines.data.filter(
    (invoiceLine) =>
      !!invoiceLine.parent?.subscription_item_details?.proration && // Non-prorated items
      invoiceLine.amount > 0 // Non-prorated items
  );
  return keptInvoiceLines.map((invoiceLine) => {
    const subItemIFromInvoice =
      invoiceLine.parent?.subscription_item_details?.subscription_item ||
      'unknown-sub-item';
    // Subscription item IDs from invoice can be newly generated for the preview invoice and can not be used as they may not exist
    const existingSubItemId = existingSubItemIds.includes(subItemIFromInvoice)
      ? subItemIFromInvoice
      : undefined;
    return {
      id: existingSubItemId,
      price: invoiceLine.pricing?.price_details?.price,
      quantity: invoiceLine.quantity || 0,
    };
  });
};

export const upsertSubscription = async ({
  requestedInvoiceLines,
}: {
  requestedInvoiceLines: SimpleCommonInvoiceSubscriptionItem[];
}) => {
  try {
    // Only send items that have a price. Items without a price are existing
    // subscription items on archived legacy prices — they aren't changing,
    // and Stripe rejects archived prices in update calls. Stripe leaves
    // unlisted existing items unchanged.
    const items = requestedInvoiceLines
      .filter((item) => item.price)
      .map((item) => ({
        id: item.id,
        price_id: item.price,
        quantity: item.quantity,
      }));
    const response = await axiosClient.put('/subscriptions/me', { items });
    return response.data;
  } catch (error) {
    console.error('Failed to upsert subscription', error);
    throw error;
  }
};
